import React, { useState, useRef, useEffect } from 'react';
import SectionHeader from '../components/SectionHeader';
import {
  btnPrimary,
  btnPrimarySmall,
  btnGhost,
  btnGhostSmall,
  tabNavContainer,
  tabNavItem,
  pillFilter,
  linkInline,
  FERTILITY_DEMO_SECTION_BORDER,
  introCardKeyline,
  introHeadlineSerifDeep,
  demoChapterStackColumnStyle,
  demoSectionCardStackStyle,
  DEMO_MINI_CARD_GRID_GAP_PX,
} from '../designSystem';
import FinancingTab from '../components/FinancingTab';

const formatCurrency = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

function barColor(you, avg) {
  if (you < avg) return '#1D9E75';
  if (you > avg * 1.15) return '#D85A30';
  return '#D4537E';
}

function barWidth(value, you, avg) {
  const max = Math.max(you, avg);
  return Math.min(100, Math.round((value / max) * 100));
}

const expenses = [
  { id: 1, name: 'Gonal-F 900 IU', date: '2025-03-01', category: 'medications', amount: 2840, avg: 3100, icon: '💊', receipt: true },
  { id: 2, name: 'Menopur 75 IU', date: '2025-03-02', category: 'medications', amount: 420, avg: 450, icon: '💊', receipt: false },
  { id: 3, name: 'Cycle monitoring visit', date: '2025-03-08', category: 'monitoring', amount: 185, avg: 200, icon: '🩺', receipt: true },
  { id: 4, name: 'Baseline labs', date: '2025-02-28', category: 'labs', amount: 320, avg: 350, icon: '🔬', receipt: false },
];

const totalSpent = 3765;
const estimatedTotal = 28500;
const remaining = estimatedTotal - totalSpent;
const vsAvg = -400;

/** Multi-select filter on My spending — empty array = include every category. */
const spendCategoryOptions = [
  { key: 'medications', label: 'Medications' },
  { key: 'procedures', label: 'Procedures' },
  { key: 'labs', label: 'Labs' },
  { key: 'monitoring', label: 'Monitoring' },
  { key: 'storage', label: 'Storage' },
];
const ALL_SPEND_CATEGORY_KEYS = spendCategoryOptions.map((o) => o.key);

const benchmarkData = {
  city: {
    label: 'Columbus, OH',
    total: 28900,
    categories: [
      { name: 'Base cycle / retrieval', you: 15000, avg: 16200 },
      { name: 'Medications (stims)', you: 3260, avg: 5500 },
      { name: 'Genetic testing (PGT)', you: 3500, avg: 2500 },
      { name: 'Monitoring visits', you: 185, avg: 200 },
      { name: 'Anesthesia', you: 800, avg: 800 },
      { name: 'Embryo storage', you: 800, avg: 700 },
    ],
  },
  state: {
    label: 'Ohio',
    total: 27400,
    categories: [
      { name: 'Base cycle / retrieval', you: 15000, avg: 15800 },
      { name: 'Medications (stims)', you: 3260, avg: 5200 },
      { name: 'Genetic testing (PGT)', you: 3500, avg: 2200 },
      { name: 'Monitoring visits', you: 185, avg: 190 },
      { name: 'Anesthesia', you: 800, avg: 750 },
      { name: 'Embryo storage', you: 800, avg: 650 },
    ],
  },
  national: {
    label: 'National avg',
    total: 28100,
    categories: [
      { name: 'Base cycle / retrieval', you: 15000, avg: 15000 },
      { name: 'Medications (stims)', you: 3260, avg: 5500 },
      { name: 'Genetic testing (PGT)', you: 3500, avg: 2500 },
      { name: 'Monitoring visits', you: 185, avg: 200 },
      { name: 'Anesthesia', you: 800, avg: 800 },
      { name: 'Embryo storage', you: 800, avg: 800 },
    ],
  },
};

const receipts = [
  { id: 1, vendor: 'CVS Pharmacy', date: 'Mar 1', amount: 2840, oraRead: 'Gonal-F 900 IU', matched: true, matchedTo: '$2,840 expense' },
  { id: 2, vendor: 'Ohio Fertility Network', date: 'Mar 8', amount: 185, oraRead: 'Monitoring visit', matched: true, matchedTo: '$185 expense' },
  { id: 3, vendor: 'Walgreens', date: 'Feb 28', amount: 320, oraRead: 'Could be baseline labs', matched: false },
];

const protocolMeds = [
  { name: 'Gonal-F (FSH)', dose: '900 IU · antagonist protocol', paid: 2840, range: '$2,500–$3,500', icon: '💉' },
  { name: 'Menopur (FSH + LH)', dose: '75 IU vials · 10 days', paid: 420, range: '$350–$600', icon: '💉' },
  { name: 'Ganirelix (antagonist)', dose: '0.25mg · days 6–10', paid: null, est: 320, range: '$280–$420', icon: '💉' },
];

const upcomingMedCosts = [
  { name: 'HCG trigger shot', note: 'One-time · before retrieval', range: '$150–$250', sub: 'Lupron trigger ~$80' },
  { name: 'Progesterone in oil (PIO)', note: 'Post-retrieval · if transferring', range: '$80–$150', sub: null },
  { name: 'Estrace (if FET)', note: 'Lining prep · frozen transfer only', range: '$40–$100', sub: null },
];

const categoryBg = { medications: '#FBEAF0', monitoring: '#E1F5EE', labs: '#EEEDFE', procedures: '#E6F1FB' };

export default function FertilityDemoFinancial({ navigate, omitHeader }) {
  const [activeTab, setActiveTab] = useState('spend');
  const [selectedSpendCategories, setSelectedSpendCategories] = useState([]);
  const [spendCategoryMenuOpen, setSpendCategoryMenuOpen] = useState(false);
  const spendCategoryFilterRef = useRef(null);
  const [benchmarkLocation, setBenchmarkLocation] = useState('city');
  const [medFilter, setMedFilter] = useState('protocol');
  const fileInputRef = useRef(null);

  useEffect(() => {
    function handleDown(e) {
      if (!spendCategoryFilterRef.current?.contains(e.target)) setSpendCategoryMenuOpen(false);
    }
    document.addEventListener('mousedown', handleDown);
    return () => document.removeEventListener('mousedown', handleDown);
  }, []);

  const isSpendCategoryChecked = (key) =>
    selectedSpendCategories.length === 0 || selectedSpendCategories.includes(key);

  const toggleSpendCategory = (key) => {
    setSelectedSpendCategories((prev) => {
      if (prev.length === 0) {
        return ALL_SPEND_CATEGORY_KEYS.filter((k) => k !== key);
      }
      if (prev.includes(key)) {
        return prev.filter((k) => k !== key);
      }
      const next = [...prev, key];
      if (next.length === ALL_SPEND_CATEGORY_KEYS.length) return [];
      return next;
    });
  };

  const spendFilterSummary = (() => {
    if (selectedSpendCategories.length === 0) return 'All categories';
    if (selectedSpendCategories.length === 1) {
      return spendCategoryOptions.find((o) => o.key === selectedSpendCategories[0])?.label ?? '1 category';
    }
    if (selectedSpendCategories.length <= 3) {
      return selectedSpendCategories
        .map((k) => spendCategoryOptions.find((o) => o.key === k)?.label)
        .filter(Boolean)
        .join(', ');
    }
    return `${selectedSpendCategories.length} categories`;
  })();

  const filteredExpenses =
    selectedSpendCategories.length === 0
      ? expenses
      : expenses.filter((e) => selectedSpendCategories.includes(e.category));
  const matchedReceipts = receipts.filter((r) => r.matched);
  const unmatchedReceipts = receipts.filter((r) => !r.matched);

  const data = benchmarkData[benchmarkLocation];

  const card = { background: 'var(--white)', border: FERTILITY_DEMO_SECTION_BORDER, borderRadius: 14, overflow: 'hidden' };

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif", ...demoChapterStackColumnStyle }}>
      {!omitHeader && (
        <div
          style={{
            background: 'linear-gradient(140deg, rgb(251,234,240), rgb(225,245,238))',
            ...introCardKeyline,
            padding: '22px 26px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div className="card-label" style={{ marginBottom: 4 }}>
                <span>Financial tracker · IVF Cycle 2</span>
              </div>
              <div style={{ ...introHeadlineSerifDeep, marginBottom: 3 }}>Your IVF costs</div>
              <div style={{ fontSize: 12, color: 'rgba(60,30,45,0.5)' }}>Track spending, upload receipts, compare to city and national averages</div>
            </div>
            <button type="button" onClick={() => navigate('/signup')} style={{ ...btnPrimary, flexShrink: 0 }}>
              + Add expense
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: DEMO_MINI_CARD_GRID_GAP_PX }}>
            <div style={{ background: 'rgba(255,255,255,0.65)', borderRadius: 10, padding: '12px 16px', border: '0.5px solid rgba(212,83,126,0.1)', textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: 'rgba(60,30,45,0.5)', marginBottom: 4 }}>Spent so far</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#D4537E' }}>{formatCurrency(totalSpent)}</div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.65)', borderRadius: 10, padding: '12px 16px', border: '0.5px solid rgba(212,83,126,0.1)', textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: 'rgba(60,30,45,0.5)', marginBottom: 4 }}>Est. total</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#2d1a22' }}>{formatCurrency(estimatedTotal)}</div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.65)', borderRadius: 10, padding: '12px 16px', border: '0.5px solid rgba(212,83,126,0.1)', textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: 'rgba(60,30,45,0.5)', marginBottom: 4 }}>Remaining</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#2d1a22' }}>{formatCurrency(remaining)}</div>
            </div>
            <div style={{ background: 'rgba(29,158,117,0.07)', borderRadius: 10, padding: '12px 16px', border: '0.5px solid rgba(29,158,117,0.15)', textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: 'rgba(10,60,35,0.5)', marginBottom: 4 }}>vs avg</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#1D9E75' }}>{vsAvg < 0 ? `−${formatCurrency(Math.abs(vsAvg))}` : formatCurrency(vsAvg)}</div>
            </div>
          </div>
        </div>
      )}

      <div style={demoSectionCardStackStyle}>
      <div style={{ ...card, padding: '16px 18px' }}>
      <SectionHeader
        title="Financial Tracker"
        descriptor="Track every cost this cycle — medications, monitoring, procedures, and lab work."
      />

      {/* 2. Tab bar */}
      <div style={{ ...tabNavContainer, marginBottom: 14, marginTop: 8, overflowX: 'auto' }}>
        {[
          { key: 'spend', label: 'My spending' },
          { key: 'meds', label: 'Medications' },
          { key: 'receipts', label: 'Receipts' },
          { key: 'benchmarks', label: 'Benchmarks' },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setActiveTab(t.key)}
            style={{ ...tabNavItem(activeTab === t.key), fontSize: 13, fontWeight: 600 }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* 3A. My spending tab */}
      {activeTab === 'spend' && (
        <div style={demoSectionCardStackStyle}>
          <div style={{ ...card, padding: '16px 18px' }}>
            <div className="card-label" style={{ marginBottom: 8 }}>
              <span>Cycle 2 spend progress</span>
              <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 'normal' }}>
                {formatCurrency(totalSpent)} of {formatCurrency(estimatedTotal)} est.
              </span>
            </div>
            <div style={{ background: 'rgba(212,83,126,0.1)', borderRadius: 999, height: 8, marginBottom: 8, overflow: 'hidden' }}>
              <div style={{ background: '#D4537E', width: `${Math.round((totalSpent / estimatedTotal) * 100)}%`, height: '100%' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--color-text-tertiary, #888)' }}>
              <span>{Math.round((totalSpent / estimatedTotal) * 100)}% complete</span>
              <span>Retrieval day — biggest cost coming</span>
            </div>
          </div>
          <div ref={spendCategoryFilterRef} style={{ position: 'relative' }}>
            <button
              type="button"
              aria-expanded={spendCategoryMenuOpen}
              aria-haspopup="listbox"
              onClick={() => setSpendCategoryMenuOpen((o) => !o)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
                minWidth: 220,
                maxWidth: '100%',
                padding: '8px 12px',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'inherit',
                color: '#2d1a22',
                background: 'var(--white)',
                border: '0.5px solid rgba(212,83,126,0.25)',
                borderRadius: 10,
                cursor: 'pointer',
                boxSizing: 'border-box',
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
                <span style={{ color: 'var(--muted)', fontWeight: 500, marginRight: 6 }}>Show:</span>
                {spendFilterSummary}
              </span>
              <span style={{ color: 'var(--pink, #d4537e)', flexShrink: 0, fontSize: 10 }} aria-hidden>
                {spendCategoryMenuOpen ? '▴' : '▾'}
              </span>
            </button>
            {spendCategoryMenuOpen && (
              <div
                role="listbox"
                aria-multiselectable="true"
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 'calc(100% + 4px)',
                  zIndex: 30,
                  minWidth: 260,
                  maxWidth: 'min(100vw - 32px, 320px)',
                  background: 'var(--white)',
                  border: '0.5px solid rgba(212,83,126,0.2)',
                  borderRadius: 10,
                  boxShadow: '0 8px 24px rgba(45, 26, 34, 0.08)',
                  padding: '8px 0',
                }}
              >
                <div style={{ padding: '4px 12px 8px', fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Categories
                </div>
                {spendCategoryOptions.map((o) => {
                  const checked = isSpendCategoryChecked(o.key);
                  return (
                    <label
                      key={o.key}
                      role="option"
                      aria-selected={checked}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '8px 14px',
                        cursor: 'pointer',
                        fontSize: 13,
                        color: '#2d1a22',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(212,83,126,0.06)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSpendCategory(o.key)}
                        style={{ width: 14, height: 14, accentColor: 'var(--pink, #d4537e)' }}
                      />
                      <span>{o.label}</span>
                    </label>
                  );
                })}
                <div style={{ borderTop: '0.5px solid rgba(212,83,126,0.1)', marginTop: 4, padding: '8px 14px 4px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedSpendCategories([]);
                      setSpendCategoryMenuOpen(false);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--pink, #d4537e)',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    Reset to all categories
                  </button>
                </div>
              </div>
            )}
          </div>
          <div style={card}>
            {filteredExpenses.map((exp, idx) => (
              <div
                key={exp.id}
                style={{
                  padding: '14px 18px',
                  borderBottom: idx < filteredExpenses.length - 1 ? '0.5px solid rgba(212,83,126,0.07)' : 'none',
                  display: 'flex',
                  gap: 12,
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: categoryBg[exp.category] || '#FBEAF0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 16,
                    flexShrink: 0,
                  }}
                >
                  {exp.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#2d1a22' }}>{exp.name}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                    <span style={{ fontSize: 11, color: 'var(--color-text-tertiary, #888)' }}>{exp.date} · {exp.category}</span>
                    {exp.receipt && (
                      <span style={{ background: '#E1F5EE', color: '#0F6E56', border: '0.5px solid rgba(29,158,117,0.2)', borderRadius: 999, padding: '2px 8px', fontSize: 10, fontWeight: 500 }}>receipt ✓</span>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#2d1a22' }}>{formatCurrency(exp.amount)}</div>
                  <div style={{ fontSize: 10, color: exp.amount < exp.avg ? '#1D9E75' : 'var(--color-text-tertiary, #888)' }}>avg {formatCurrency(exp.avg)}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <button type="button" onClick={() => navigate('/signup')} style={{ ...linkInline, background: 'none', border: 'none', padding: 0 }}>
              + Add expense manually
            </button>
          </div>
        </div>
      )}

      {/* 3B. Medications & guides tab */}
      {activeTab === 'meds' && (
        <div style={demoSectionCardStackStyle}>
          <p style={{ fontSize: 12, color: 'var(--color-text-tertiary, #888)', lineHeight: 1.7, marginBottom: 12 }}>
            Estimated costs for common IVF medications. Prices vary by pharmacy, insurance, and dose. Use these as planning benchmarks.
          </p>
          <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
            <button type="button" onClick={() => setMedFilter('protocol')} style={pillFilter(medFilter === 'protocol')}>Your protocol</button>
            <button type="button" onClick={() => setMedFilter('all')} style={pillFilter(medFilter === 'all')}>All medications</button>
          </div>
          {(medFilter === 'protocol' || medFilter === 'all') && (
            <div style={demoSectionCardStackStyle}>
              <div className="card-label">
                <span>Your current protocol — Cycle 2</span>
              </div>
              <div style={card}>
                {protocolMeds.map((med, idx) => (
                  <div key={med.name} style={{ padding: '14px 18px', borderBottom: idx < protocolMeds.length - 1 ? '0.5px solid rgba(212,83,126,0.07)' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: '#2d1a22' }}>{med.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-tertiary, #888)' }}>{med.dose}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {med.paid ? (
                        <>
                          <div style={{ fontSize: 13, fontWeight: 500, color: '#D4537E' }}>{formatCurrency(med.paid)} <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--color-text-tertiary, #888)' }}>you paid</span></div>
                          <div style={{ fontSize: 10, color: 'var(--color-text-tertiary, #888)' }}>range {med.range}</div>
                        </>
                      ) : (
                        <>
                          <div style={{ fontSize: 13, fontWeight: 500, color: '#2d1a22' }}>est. {formatCurrency(med.est)}</div>
                          <div style={{ fontSize: 10, color: 'var(--color-text-tertiary, #888)' }}>range {med.range}</div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="card-label">
                <span>Estimated upcoming costs — this cycle</span>
              </div>
              <div style={card}>
                {upcomingMedCosts.map((med, idx) => (
                  <div key={med.name} style={{ padding: '14px 18px', borderBottom: idx < upcomingMedCosts.length - 1 ? '0.5px solid rgba(212,83,126,0.07)' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: '#2d1a22' }}>{med.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-tertiary, #888)' }}>{med.note}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: '#2d1a22' }}>{med.range}</div>
                      {med.sub && <div style={{ fontSize: 10, color: 'var(--color-text-tertiary, #888)' }}>{med.sub}</div>}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ background: '#FBEAF0', border: '0.5px solid rgba(212,83,126,0.15)', borderRadius: 12, padding: '14px 16px', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>💡</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 500, color: '#2d1a22', marginBottom: 4 }}>Save on medications</div>
                  <p style={{ fontSize: 11, color: 'rgba(60,30,45,0.65)', lineHeight: 1.65, margin: 0 }}>
                    Compassionate care programs (EMD Serono, Ferring) can reduce costs 50–75% based on income. Ask your clinic about MDR Pharmacy or Freedom Fertility — often cheaper than retail pharmacies.
                  </p>
                  <button type="button" onClick={() => navigate('/signup')} style={{ ...linkInline, background: 'none', border: 'none', padding: 0, marginTop: 4 }}>Learn more →</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 3C. Receipts tab */}
      {activeTab === 'receipts' && (
        <div style={demoSectionCardStackStyle}>
          <p style={{ fontSize: 12, color: 'var(--color-text-tertiary, #888)', lineHeight: 1.7, marginBottom: 12 }}>
            Upload receipts from your phone or computer. Nova reads them automatically and categorizes your spend.
          </p>
          <div
            role="button"
            tabIndex={0}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
            style={{
              border: '1.5px dashed rgba(212,83,126,0.3)',
              borderRadius: 12,
              padding: 24,
              textAlign: 'center',
              background: 'rgba(251,234,240,0.2)',
              cursor: 'pointer',
              transition: 'background 0.2s, border-color 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(251,234,240,0.5)'; e.currentTarget.style.borderColor = 'rgba(212,83,126,0.5)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(251,234,240,0.2)'; e.currentTarget.style.borderColor = 'rgba(212,83,126,0.3)'; }}
          >
            <input ref={fileInputRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={() => {}} />
            <div style={{ fontSize: 22, marginBottom: 8 }}>📸</div>
            <div style={{ fontSize: 13, fontWeight: 500, color: '#2d1a22', marginBottom: 4 }}>Drop a receipt here or tap to upload</div>
            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary, #888)', marginBottom: 12 }}>JPG, PNG, or PDF · Nova will read and categorize it automatically</div>
            <button type="button" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }} style={btnPrimary}>
              Choose file
            </button>
            <div style={{ fontSize: 10, color: 'rgba(60,30,45,0.4)', marginTop: 8 }}>Or take a photo on your phone — share it directly to OrivonAI</div>
          </div>
          {matchedReceipts.length > 0 && (
            <>
              <div className="card-label">
                <span>Uploaded receipts ({matchedReceipts.length})</span>
              </div>
              <div style={card}>
                {matchedReceipts.map((r, idx) => (
                  <div key={r.id} style={{ padding: '13px 18px', borderBottom: idx < matchedReceipts.length - 1 ? '0.5px solid rgba(212,83,126,0.07)' : 'none', display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 8, background: 'var(--color-background-secondary, #f5f5f5)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>🧾</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: '#2d1a22' }}>{r.vendor} · {r.date}</div>
                      <div style={{ fontSize: 10, color: 'var(--color-text-tertiary, #888)' }}>Nova read: {r.oraRead}</div>
                      <span style={{ display: 'inline-flex', marginTop: 4, background: '#E1F5EE', color: '#0F6E56', border: '0.5px solid rgba(29,158,117,0.2)', borderRadius: 999, padding: '2px 9px', fontSize: 10, fontWeight: 500 }}>✓ Matched to {r.matchedTo}</span>
                    </div>
                    <button type="button" onClick={() => navigate('/signup')} style={{ ...linkInline, background: 'none', border: 'none', padding: 0 }}>View</button>
                  </div>
                ))}
              </div>
            </>
          )}
          {unmatchedReceipts.length > 0 && (
            <>
              <div className="card-label">
                <span>Needs review ({unmatchedReceipts.length})</span>
              </div>
              <div style={card}>
                {unmatchedReceipts.map((r) => (
                  <div key={r.id} style={{ padding: '13px 18px', display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 8, background: '#FEF0EB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>🧾</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: '#2d1a22' }}>{r.vendor} · {r.date}</div>
                      <div style={{ fontSize: 10, color: 'var(--color-text-tertiary, #888)' }}>Nova read: {r.oraRead}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      <button type="button" onClick={() => navigate('/signup')} style={btnPrimarySmall}>Match</button>
                      <button type="button" onClick={() => navigate('/signup')} style={btnGhostSmall}>Skip</button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* 3D. Benchmarks tab */}
      {activeTab === 'benchmarks' && (
        <div style={demoSectionCardStackStyle}>
          <p style={{ fontSize: 12, color: 'var(--color-text-tertiary, #888)', lineHeight: 1.7, marginBottom: 12 }}>
            How your costs compare to women in your city, state, and nationally. Based on anonymized data from OrivonAI members.
          </p>
          <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => setBenchmarkLocation('city')} style={pillFilter(benchmarkLocation === 'city')}>Columbus, OH</button>
            <button type="button" onClick={() => setBenchmarkLocation('state')} style={pillFilter(benchmarkLocation === 'state')}>Ohio</button>
            <button type="button" onClick={() => setBenchmarkLocation('national')} style={pillFilter(benchmarkLocation === 'national')}>National</button>
          </div>
          <div style={{ ...card, padding: '16px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: '#2d1a22' }}>Cycle 2 — full cost estimate</span>
              <div style={{ display: 'flex', gap: 10, fontSize: 10, color: 'var(--color-text-tertiary, #888)' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 6, height: 6, borderRadius: '50%', background: '#D4537E' }} /> Your estimate</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 6, height: 6, borderRadius: '50%', background: 'rgba(212,83,126,0.3)' }} /> {data.label} avg</span>
              </div>
            </div>
            {data.categories.map((cat) => (
              <div key={cat.name} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 500, color: '#2d1a22' }}>{cat.name}</span>
                  <div style={{ display: 'flex', gap: 10, fontSize: 11 }}>
                    <span style={{ color: barColor(cat.you, cat.avg), fontWeight: 500 }}>{formatCurrency(cat.you)}</span>
                    <span style={{ color: 'var(--color-text-tertiary, #888)' }}>avg {formatCurrency(cat.avg)}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <div style={{ flex: 1, background: 'rgba(212,83,126,0.1)', borderRadius: 999, height: 7, overflow: 'hidden' }}>
                    <div style={{ background: barColor(cat.you, cat.avg), width: `${barWidth(cat.you, cat.you, cat.avg)}%`, height: '100%' }} />
                  </div>
                  <span style={{ fontSize: 10, color: barColor(cat.you, cat.avg), width: 30, textAlign: 'right' }}>you</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ flex: 1, background: 'rgba(212,83,126,0.1)', borderRadius: 999, height: 7, overflow: 'hidden' }}>
                    <div style={{ background: 'rgba(212,83,126,0.25)', width: `${barWidth(cat.avg, cat.you, cat.avg)}%`, height: '100%' }} />
                  </div>
                  <span style={{ fontSize: 10, color: 'var(--color-text-tertiary, #888)', width: 30, textAlign: 'right' }}>avg</span>
                </div>
              </div>
            ))}
            <div style={{ height: '0.5px', background: 'var(--color-border-tertiary, #e5e5e5)', margin: '10px 0' }} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: '#2d1a22' }}>Your est. total vs {data.label}</span>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 16, fontWeight: 500, color: estimatedTotal < data.total ? '#1D9E75' : '#D85A30' }}>
                  {formatCurrency(estimatedTotal)} vs {data.total.toLocaleString()}
                </div>
                <div style={{ fontSize: 10, color: estimatedTotal < data.total ? '#1D9E75' : '#D85A30' }}>
                  {estimatedTotal < data.total ? `You're $${(data.total - estimatedTotal).toLocaleString()} below average 🎉` : `You're $${(estimatedTotal - data.total).toLocaleString()} above average`}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      </div>

      <div style={{ ...card, padding: '16px 18px' }}>
        <SectionHeader
          title="Financing Options"
          descriptor="Medical loans, clinic payment plans, and grants matched to your cycle estimate and what you’ve spent so far."
        />
        <div style={{ marginTop: 8 }}>
          <FinancingTab navigate={navigate} />
        </div>
      </div>
      </div>
    </div>
  );
}
